import { intentKey } from './intent';
import { clubPath } from '../state/club-session';
/**
 * Market API Service (Microservicio de Mercado y Fichajes)
 * Conecta con los endpoints del backend y en caso de que aún no existan o fallen,
 * simula la llamada cargando el mock (.json) desde /mocks/market/
 */
import {
  BackendMarketListingResponse,
  BackendClubListing,
  BackendClubTransfer,
  MarketOfferResponse,
  MarketOfferFailure,
  BackendActiveOffersResponse,
  BackendHistoricalOffersResponse,
  PortfolioResponse,
  PortfolioItem,
  Player,
  ReleaseClauseAttemptPayload,
  ReleaseClauseAttemptResponse,
  ContractRenewalPayload,
  ContractRenewalResponse,
} from '../models/types';
import { portfolioSimulation } from '../simulation/portfolio-simulation';
import { apiSimulator } from './api-simulator.service';

const pendingClubTransfers = new Map<string, Promise<BackendClubTransfer[]>>();

export const marketApi = {
  async cancelClubOffer(id: string, expectedVersion: number): Promise<BackendClubTransfer> {
    return apiSimulator.callWithMockFallback({
      method: 'PUT', endpoint: clubPath(`/market/club-transfers/${encodeURIComponent(id)}/cancellation`),
      body: { expectedVersion }, service: 'market', mockName: 'cancel-club-offer', allowMockFallback: false,
    });
  },
  async updateClubOffer(id: string, payload: {
    updateId: string; expectedVersion: number;
    transferAmount: number; annualSalary: number; releaseClause: number; termSeasons: number;
  }): Promise<BackendClubTransfer> {
    return apiSimulator.callWithMockFallback({
      method: 'PUT', endpoint: clubPath(`/market/club-transfers/${encodeURIComponent(id)}`),
      body: payload, service: 'market', mockName: 'update-club-offer', allowMockFallback: false,
    });
  },
  async getClubTransfers(clubId: string): Promise<BackendClubTransfer[]> {
    const pending = pendingClubTransfers.get(clubId);
    if (pending) return pending;
    const request = apiSimulator.callWithMockFallback<BackendClubTransfer[]>({
      method: 'GET', endpoint: clubPath('/market/club-transfers',clubId),
      service: 'market', mockName: 'club-transfers', allowMockFallback: false,
    });
    pendingClubTransfers.set(clubId, request);
    try {
      return await request;
    } finally {
      pendingClubTransfers.delete(clubId);
    }
  },
  async getClubTransfer(id: string, clubId: string): Promise<BackendClubTransfer> {
    return apiSimulator.callWithMockFallback({
      method: 'GET', endpoint: clubPath(`/market/club-transfers/${encodeURIComponent(id)}`,clubId),
      service: 'market', mockName: 'club-transfers', allowMockFallback: false,
    });
  },
  async submitSellerDecision(id: string, payload: {
    expectedVersion: number;
    accept: boolean;
  }): Promise<BackendClubTransfer> {
    return apiSimulator.callWithMockFallback({
      method: 'PUT', endpoint: clubPath(`/market/club-transfers/${encodeURIComponent(id)}/seller-decision`),
      body: payload, service: 'market', mockName: 'club-transfers', allowMockFallback: false,
    });
  },
  async submitClubOffer(listingId: string, payload: {
    operationId: string; playerInstanceId: string; buyerClubId: string;
    transferAmount: number; annualSalary: number; releaseClause: number; termSeasons: number;
  }): Promise<{ id: string; status: string; releaseOutcome?: string; pendingReason?: string; failure?: MarketOfferFailure }> {
    return apiSimulator.callWithMockFallback({
      method: 'POST', endpoint: clubPath(`/market/club-listings/${encodeURIComponent(listingId)}/offers`),
      body: payload, service: 'market', mockName: 'submit-offer', allowMockFallback: false,
    });
  },

  async createClubListing(playerInstanceId: string, askingPrice: number): Promise<Omit<BackendClubListing, 'player'>> {
    const intent=await intentKey('create-listing',{playerInstanceId,askingPrice});
    const result=await apiSimulator.callWithMockFallback<Omit<BackendClubListing,'player'>>({
      headers:{'Idempotency-Key':intent.key},
      method: 'POST', endpoint: clubPath('/market/club-listings'),
      body: { playerInstanceId, askingPrice }, service: 'market',
      mockName: 'club-listings', allowMockFallback: false,
    });
    intent.complete(); return result;
  },

  async cancelClubListing(listingId: string): Promise<Omit<BackendClubListing, 'player'>> {
    return apiSimulator.callWithMockFallback({
      method: 'PUT', endpoint: clubPath(`/market/club-listings/${encodeURIComponent(listingId)}/cancellation`),
      service: 'market', mockName: 'club-listings', allowMockFallback: false,
    });
  },

  async getClubListings(competitionUnitId: string): Promise<BackendClubListing[]> {
    if (!competitionUnitId || competitionUnitId === 'league-global-01') {
      throw new Error('Se requiere una unidad de competición válida para consultar los transferibles.');
    }
    return apiSimulator.callWithMockFallback<BackendClubListing[]>({
      method: 'GET',
      endpoint: clubPath('/market/club-listings',competitionUnitId),
      service: 'market',
      mockName: 'club-listings',
      allowMockFallback: false,
    });
  },

  /**
   * GET /api/market/offers?status=active
   * Endpoint oficial para obtener las ofertas activas en el mercado.
   * Mock fallback: /mocks/market/active-offers.json
   */
  async getActiveOffers(status: string = 'active'): Promise<BackendActiveOffersResponse> {
    const endpoint = clubPath(`/market/offers?status=${encodeURIComponent(status)}`);
    return apiSimulator.callWithMockFallback<BackendActiveOffersResponse>({
      method: 'GET',
      endpoint,
      service: 'market',
      mockName: 'active-offers', allowMockFallback: false,
    });
  },

  /**
   * GET /api/market/offers/history
   * Historial paginado de ofertas por jugadores libres y con contrato.
   * Mock fallback: /mocks/market/offers-history.json
   */
  async getOffersHistory(page: number = 0, size: number = 50): Promise<BackendHistoricalOffersResponse> {
    const endpoint = clubPath(`/market/offers/history?page=${page}&size=${size}`);
    return apiSimulator.callWithMockFallback<BackendHistoricalOffersResponse>({
      method: 'GET',
      endpoint,
      service: 'market',
      mockName: 'offers-history', allowMockFallback: false,
    });
  },

  /**
   * GET /api/market/units/{competitionUnitId}/current-listing
   * Endpoint oficial para obtener los agentes libres de la unidad de competición.
   * Mock fallback: /mocks/market/current-listings.json
   */
  async getCurrentListing(
    competitionUnitId: string
  ): Promise<BackendMarketListingResponse> {
    if (!competitionUnitId || competitionUnitId === 'league-global-01') {
      throw new Error('Se requiere un competitionUnitId válido para consultar el mercado.');
    }
    const targetUnitId = competitionUnitId;

    const primaryEndpoint = clubPath('/market/current-listing',competitionUnitId);
    return apiSimulator.callWithMockFallback<BackendMarketListingResponse>({
      method: 'GET',
      endpoint: primaryEndpoint,
      service: 'market',
      mockName: 'current-listings',
      transformMock: (mock) => ({
        ...mock,
        unitId: targetUnitId,
      }),
    });
  },

  /**
   * POST /api/market/listings/{listingId}/players/{playerInstanceId}/offers
   * Endpoint oficial para presentar una oferta formal por un jugador en el mercado.
   * Mock fallback: /mocks/market/submit-offer.json
   */
  async submitPlayerOffer(
    listingId: string,
    playerInstanceId: string,
    payload: {
      transferAmount: number;
      annualSalary: number;
      releaseClause: number;
      termSeasons: number;
    }
  ): Promise<MarketOfferResponse> {
    const endpoint = clubPath(`/market/listings/${encodeURIComponent(listingId)}/players/${encodeURIComponent(playerInstanceId)}/offers`);
    const intent=await intentKey('free-offer',{listingId,playerInstanceId,payload});
    const result=await apiSimulator.callWithMockFallback<MarketOfferResponse>({
      headers:{'Idempotency-Key':intent.key}, allowMockFallback:false,
      method: 'POST',
      endpoint,
      body: payload,
      service: 'market',
      mockName: 'submit-offer',
      transformMock: (mock) => {
        // Simular validación de saldo en caso de mock fallback
        let availableEur = 12000000.00;
        try {
          if (typeof localStorage !== 'undefined') {
            const storedClubs = localStorage.getItem('football_manager_real_v01_clubs');
            if (storedClubs) {
              const parsed = JSON.parse(storedClubs);
              const userC = parsed.find((c: any) => c.isUser);
              if (userC?.economy?.availableBalance !== undefined) {
                availableEur = userC.economy.availableBalance * 1_000_000;
              }
            }
          }
        } catch {}

        if (payload.transferAmount > availableEur) {
          const err: any = new Error('Insufficient available balance to submit this offer.');
          err.status = 409;
          err.failure = {
            code: 'FUNDS_RESERVATION_REJECTED',
            message: 'Insufficient available balance to submit this offer.',
            requestedAmount: payload.transferAmount,
            availableAmount: availableEur,
          };
          throw err;
        }

        return {
          ...mock,
          listingId,
          playerInstanceId,
          terms: {
            transferAmount: payload.transferAmount,
            annualSalary: payload.annualSalary,
            releaseClause: payload.releaseClause,
            termSeasons: payload.termSeasons,
          },
          submittedAt: new Date().toISOString(),
        };
      },
    });
    intent.complete(); return result;
  },

  /**
   * PUT /api/market/offers/{offerId}
   * Endpoint oficial para actualizar / modificar una oferta formal activa por un futbolista (PUT).
   * Mismo endpoint base que cancelar oferta (/api/market/offers/{offerId}/cancellation) pero sin /cancellation.
   * Mock fallback: /mocks/market/update-offer.json
   */
  async updatePlayerOffer(
    offerIdOrListingId: string,
    payloadOrPlayerInstanceId: any,
    listingIdOrPayload?: any,
    offerIdOrPlayerInstanceId?: any
  ): Promise<MarketOfferResponse> {
    let resolvedOfferId = '';
    let payload: {
      transferAmount: number;
      annualSalary: number;
      releaseClause: number;
      termSeasons: number;
    };
    let listingId = '';
    let playerInstanceId = '';

    if (
      payloadOrPlayerInstanceId &&
      typeof payloadOrPlayerInstanceId === 'object' &&
      'transferAmount' in payloadOrPlayerInstanceId
    ) {
      // Firma preferida: updatePlayerOffer(offerId, payload, listingId?, playerInstanceId?)
      resolvedOfferId = offerIdOrListingId;
      payload = payloadOrPlayerInstanceId;
      listingId = listingIdOrPayload || '';
      playerInstanceId = offerIdOrPlayerInstanceId || '';
    } else {
      // Firma alternativa: updatePlayerOffer(listingId, playerInstanceId, payload, offerId)
      listingId = offerIdOrListingId;
      playerInstanceId = payloadOrPlayerInstanceId;
      payload = listingIdOrPayload;
      resolvedOfferId = offerIdOrPlayerInstanceId || '';
    }

    // Endpoint directo: PUT /api/market/offers/{offerId}
    const endpoint = resolvedOfferId
      ? clubPath(`/market/offers/${encodeURIComponent(resolvedOfferId)}`)
      : clubPath(`/market/listings/${encodeURIComponent(listingId)}/players/${encodeURIComponent(playerInstanceId)}/offers`);

    const buildMockTransform = (mock: any) => {
      let availableEur = 12000000.00;
      try {
        if (typeof localStorage !== 'undefined') {
          const storedClubs = localStorage.getItem('football_manager_real_v01_clubs');
          if (storedClubs) {
            const parsed = JSON.parse(storedClubs);
            const userC = parsed.find((c: any) => c.isUser);
            if (userC?.economy?.availableBalance !== undefined) {
              availableEur = userC.economy.availableBalance * 1_000_000;
            }
          }
        }
      } catch {}

      if (payload && payload.transferAmount > availableEur) {
        const err: any = new Error('Insufficient available balance to update this offer.');
        err.status = 409;
        err.failure = {
          code: 'FUNDS_RESERVATION_REJECTED',
          message: 'Insufficient available balance to update this offer.',
          requestedAmount: payload.transferAmount,
          availableAmount: availableEur,
        };
        throw err;
      }

      return {
        ...mock,
        id: resolvedOfferId || mock?.id || `off-${Date.now()}`,
        listingId: listingId || mock?.listingId,
        playerInstanceId: playerInstanceId || mock?.playerInstanceId,
        terms: {
          transferAmount: payload?.transferAmount,
          annualSalary: payload?.annualSalary,
          releaseClause: payload?.releaseClause,
          termSeasons: payload?.termSeasons,
        },
        status: 'SUBMITTED',
        submittedAt: new Date().toISOString(),
      };
    };

    return await apiSimulator.callWithMockFallback<MarketOfferResponse>({
      method: 'PUT',
      endpoint,
      body: payload,
      service: 'market',
      mockName: 'update-offer',
      transformMock: buildMockTransform,
    });
  },

  /**
   * PUT /api/market/offers/{offerId}/cancellation
   * Cancela una oferta formal de traspaso activa.
   * Mock fallback: /mocks/market/cancel-offer.json
   */
  async cancelOffer(offerId: string): Promise<{ success: boolean; message: string; offerId: string; status: string }> {
    const endpoint = clubPath(`/market/offers/${encodeURIComponent(offerId)}/cancellation`);
    return apiSimulator.callWithMockFallback<{ success: boolean; message: string; offerId: string; status: string }>({
      method: 'PUT',
      endpoint,
      service: 'market',
      mockName: 'cancel-offer',
      transformMock: (mock) => ({
        success: true,
        message: mock?.message || 'Oferta cancelada exitosamente.',
        offerId,
        status: mock?.status || 'CANCELLED',
      }),
    });
  },

  /**
   * POST /api/v1/leagues/:leagueId/market/offers
   * Mock fallback: /mocks/market/submit-offer.json
   */
  async submitOffer(
    leagueId: string,
    offerPayload: {
      playerId: string;
      transferFeeM: number;
      salaryM: number;
      durationYears: number;
      clauseM: number;
      isBuyout?: boolean;
    }
  ): Promise<{ success: boolean; message: string; offerId: string }> {
    return apiSimulator.callWithMockFallback({
      method: 'POST',
      endpoint: `/api/v1/leagues/${encodeURIComponent(leagueId)}/market/offers`,
      body: offerPayload,
      service: 'market',
      mockName: 'submit-offer',
      transformMock: (mock) => ({
        ...mock,
        message: offerPayload.isBuyout
          ? 'Pago de cláusula de rescisión depositado en LFP.'
          : 'Oferta formal de traspaso remitida al club propietario.',
        offerId: `off-${Date.now()}`,
      }),
    });
  },

  /**
   * POST /api/v1/leagues/:leagueId/market/buyout-clause
   * Mock fallback: /mocks/market/buyout-clause.json
   */
  async buyoutClause(
    leagueId: string,
    playerId: string,
    clauseM: number
  ): Promise<{ success: boolean; message: string }> {
    return apiSimulator.callWithMockFallback({
      method: 'POST',
      endpoint: `/api/v1/leagues/${encodeURIComponent(leagueId)}/market/buyout-clause`,
      body: { playerId, clauseM },
      service: 'market',
      mockName: 'buyout-clause',
      transformMock: (mock) => ({
        ...mock,
        message: `Depósito notarial de ${clauseM.toFixed(1)} M€ completado con éxito.`,
      }),
    });
  },

  /**
   * GET /api/v1/leagues/:leagueId/market/free-agents
   * Mock fallback: /mocks/market/free-agents.json
   */
  async getDailyMarket(
    leagueId: string
  ): Promise<{ success: boolean; totalAvailable: number }> {
    return apiSimulator.callWithMockFallback({
      method: 'GET',
      endpoint: `/api/v1/leagues/${encodeURIComponent(leagueId)}/market/free-agents`,
      service: 'market',
      mockName: 'free-agents',
    });
  },

  /**
   * PUT /api/v1/leagues/:leagueId/players/:playerId/transfer-status
   * Mock fallback: /mocks/market/transfer-status.json
   */
  async updateTransferListing(
    leagueId: string,
    playerId: string,
    listed: boolean,
    askingPrice?: number
  ): Promise<{ success: boolean; message: string }> {
    return apiSimulator.callWithMockFallback({
      method: 'PUT',
      endpoint: `/api/v1/leagues/${encodeURIComponent(leagueId)}/players/${encodeURIComponent(playerId)}/transfer-status`,
      body: { listed, askingPrice },
      service: 'market',
      mockName: 'transfer-status',
      transformMock: (mock) => ({
        ...mock,
        message: listed
          ? `Jugador puesto en el mercado por ${askingPrice ?? 0} M€`
          : 'Jugador retirado del mercado',
      }),
    });
  },

  /**
   * GET /api/clubs/{clubId}/market/portfolio
   * Consultar cartera de futbolistas y estado de descubrimiento
   */
  async getPortfolio(clubId?: string): Promise<any> {
    return apiSimulator.callWithMockFallback<any>({
      method: 'GET',
      endpoint: clubPath('/market/portfolio', clubId),
      service: 'market',
      mockName: 'portfolio',
      allowMockFallback: true,
      transformMock: () => {
        const id = clubId || 'default';
        const sim = portfolioSimulation.getPortfolio(id);
        return sim?.items || [];
      },
    });
  },

  /**
   * PUT /api/clubs/{clubId}/market/portfolio/{playerId}
   * Añadir jugador a la cartera del club (máximo 3)
   */
  async addToPortfolio(playerId: string, clubId?: string, playerObj?: Player): Promise<PortfolioItem> {
    if (playerObj) {
      portfolioSimulation.registerPlayer(playerObj);
    }
    return apiSimulator.callWithMockFallback<PortfolioItem>({
      method: 'PUT',
      endpoint: clubPath(`/market/portfolio/${encodeURIComponent(playerId)}`, clubId),
      service: 'market',
      mockName: 'portfolio',
      allowMockFallback: true,
      transformMock: () => {
        const id = clubId || 'default';
        return portfolioSimulation.addToPortfolio(id, playerId, (lookupId) => {
          if (playerObj && (lookupId === playerObj.id || lookupId === playerObj.playerInstanceId)) {
            return playerObj;
          }
          return undefined;
        }, id);
      },
    });
  },

  /**
   * DELETE /api/clubs/{clubId}/market/portfolio/{playerId}
   * Retirar jugador de la cartera del club
   */
  async removeFromPortfolio(playerId: string, clubId?: string): Promise<{ success: boolean; playerId: string }> {
    return apiSimulator.callWithMockFallback<{ success: boolean; playerId: string }>({
      method: 'DELETE',
      endpoint: clubPath(`/market/portfolio/${encodeURIComponent(playerId)}`, clubId),
      service: 'market',
      mockName: 'portfolio',
      allowMockFallback: true,
      transformMock: () => {
        const id = clubId || 'default';
        return portfolioSimulation.removeFromPortfolio(id, playerId);
      },
    });
  },

  /**
   * POST /api/clubs/{clubId}/market/release-clause-attempts
   * Presentar clausulazo por un jugador cuya cláusula ha sido descubierta
   */
  async submitReleaseClauseAttempt(
    payload: ReleaseClauseAttemptPayload,
    clubId?: string
  ): Promise<ReleaseClauseAttemptResponse> {
    return apiSimulator.callWithMockFallback<ReleaseClauseAttemptResponse>({
      method: 'POST',
      endpoint: clubPath('/market/release-clause-attempts', clubId),
      body: payload,
      service: 'market',
      mockName: 'submit-offer',
      allowMockFallback: true,
      transformMock: () => {
        const id = clubId || 'default';
        return portfolioSimulation.submitReleaseClauseAttempt(id, payload, () => undefined, 999_999_999);
      },
    });
  },

  /**
   * POST /api/clubs/{clubId}/market/contract-renewals
   * Proponer renovación de contrato para un futbolista de la propia plantilla
   */
  async submitContractRenewal(
    payload: ContractRenewalPayload,
    clubId?: string
  ): Promise<ContractRenewalResponse> {
    const terms = {
      playerInstanceId: payload.playerId,
      annualSalary: payload.annualSalary,
      releaseClause: payload.releaseClause ?? payload.newReleaseClause,
      termSeasons: payload.termSeasons ?? payload.contractDurationYears ?? 1,
    };
    const endpoint = clubPath('/market/contract-renewals', clubId);
    const intent = await intentKey('contract-renewal', { endpoint, ...terms });
    const result = await apiSimulator.callWithMockFallback<BackendClubTransfer>({
      method: 'POST', endpoint,
      body: { operationId: intent.key, ...terms },
      service: 'market', mockName: 'submit-offer', allowMockFallback: false,
    });
    const status = result.status === 'CONTRACT_PENDING' ? 'PENDING'
      : result.status === 'COMPLETED' ? 'ACCEPTED'
      : ['REJECTED', 'FAILED', 'CANCELLED', 'EXPIRED'].includes(result.status) ? 'REJECTED' : null;
    if (!status) throw new Error('Respuesta inesperada al presentar la renovación. Puedes reintentar.');
    intent.complete();
    return {
      id: result.id, status, type: 'CONTRACT_RENEWAL',
      playerId: result.playerInstanceId, clubId: result.sellerClubId,
      offeredContract: {
        annualSalary: result.terms.annualSalary ?? terms.annualSalary,
        releaseClause: result.terms.releaseClause ?? terms.releaseClause ?? 0,
        termSeasons: result.terms.termSeasons ?? terms.termSeasons,
      },
      submittedAt: result.submittedAt!,
    };
  },
};
